import {
  OrderGetResponseType,
  SupplierGetResponseType,
  SupplierInvoiceCreationStageType,
  SupplierRelationshipGetResponseType,
  SupplierRelationshipPaymentDueCalculateFromType,
  SupplierRelationshipPaymentTermsRequestListResponseType,
} from "@sellar-io/api-v2/types";
import { ReactNode } from "react";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { useInvoiceCreationStage } from "./useInvoiceCreationStage";
import {
  IndicatorTypeType,
  usePaymentTermsIndicatorDirect,
} from "./usePaymentTermsIndicatorDirect";
import { useCreditTermsText } from "./useCreditTermsText";

export const usePaymentTermsIndicator = ({
  connection,
  invoiceCreationStage,
  approved = true,
  paymentTermsRequest,
  hasDefaultPriceTier = false,
  supplier,
  paymentType,
}: {
  connection:
    | Pick<
        SupplierRelationshipGetResponseType,
        "paymentTerms" | "paymentDueCalculateFrom" | "paymentDue"
      >
    | null
    | undefined;
  invoiceCreationStage: SupplierInvoiceCreationStageType;
  paymentTermsRequest?: {
    paymentDueCalculateFrom: SupplierRelationshipPaymentDueCalculateFromType;
    paymentDue: number;
    status: SupplierRelationshipPaymentTermsRequestListResponseType[number]["status"];
  } | null;
  approved?: boolean;
  hasDefaultPriceTier?: boolean;
  supplier?:
    | Pick<SupplierGetResponseType, "hasOptedOutSellarCredit">
    | undefined;
  paymentType?: OrderGetResponseType["paymentType"];
}): {
  backgroundColor: string | null;
  mainPaymentOption: string | null;
  type: IndicatorTypeType | null;
  textColor: string | null;
  borderColor: string | null;
  compact: ReactNode | null;
  full: ReactNode | null;
  fullTextOnly: ReactNode | null;
  supplierCard: ReactNode | null;
} => {
  const { business } = useSellarCurrentBusiness();

  const toolTipContent = useInvoiceCreationStage({
    isBuyer: !!business?.Retailer,
    invoiceCreationStage,
    paymentTerms: connection?.paymentTerms,
    paymentType: paymentType ?? null,
  });
  const creditTermsText = useCreditTermsText({ connection });

  const retailerCreditFlagEnabled = useRemoteFeatureFlags().isActive(
    "retailerSellarCreditCheckout",
  );

  const hasUnapprovedConnectionWithDPT =
    !!connection && hasDefaultPriceTier && !approved;
  const isApprovedConnectionWithProformaCreditTerms =
    (connection?.paymentTerms === "proforma" || !connection?.paymentTerms) &&
    approved;

  const sellarCreditEnabled =
    retailerCreditFlagEnabled &&
    business?.BusinessUser?.canAccessSellarCredit &&
    !!business.Retailer &&
    !supplier?.hasOptedOutSellarCredit &&
    (paymentType === undefined || paymentType === "sellarCredit");

  const getIndicatorOptions = (): {
    indicatorType?: IndicatorTypeType;
    overrideText?: string;
    toolTipContent?: ReactNode;
  } => {
    if (sellarCreditEnabled) {
      return { indicatorType: "sellarCredit" };
    }

    if (paymentTermsRequest?.status === "pending") {
      return { indicatorType: "termsRequested" };
    }

    if (
      isApprovedConnectionWithProformaCreditTerms ||
      hasUnapprovedConnectionWithDPT
    ) {
      return { indicatorType: "proForma" };
    }

    if (!approved && business?.Supplier) {
      return { indicatorType: "awaitingTerms" };
    }

    if (connection?.paymentTerms === "credit") {
      return {
        indicatorType: "credit",
        overrideText: creditTermsText,
        toolTipContent,
      };
    }

    if (connection?.paymentTerms === "paymentOnDelivery" && approved) {
      return { indicatorType: "paymentOnDelivery" };
    }

    if (business?.Retailer) {
      if (hasDefaultPriceTier) {
        return { indicatorType: "default" };
      }

      return { indicatorType: "requestPricing" };
    }

    return {};
  };

  const indicatorOptions = getIndicatorOptions();

  return usePaymentTermsIndicatorDirect(indicatorOptions);
};
